import React from 'react'
import PropTypes from 'prop-types'

/**
 * Error Boundary for PaymentStep component
 * Catches and handles errors in the payment flow gracefully
 */
class PaymentErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(_error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console for debugging
    console.error(
      'PaymentStep Error Boundary caught an error:',
      error,
      errorInfo,
    )

    // Store error details in state
    this.setState({
      error,
      errorInfo,
    })

    // Log to error reporting service in production
    // In a real app, you might send this to Sentry, LogRocket, etc.

    if (process.env.NODE_ENV === 'production') {
      // Example: logErrorToService(error, errorInfo)
    }
  }

  handleReset = () => {
    // Reset error boundary state
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        <div className="registration-step">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Payment Error</h4>
            <p>
              We encountered an unexpected error while processing your payment.
              Please try again or contact support if the problem persists.
            </p>
            <hr />
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={this.handleReset}
              >
                Try Again
              </button>
              {this.props.onBack && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={this.props.onBack}
                >
                  Go Back
                </button>
              )}
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-3" style={{ whiteSpace: 'pre-wrap' }}>
                <summary>Error Details (Development Only)</summary>
                <p className="mb-1">
                  <strong>Error:</strong> {this.state.error.toString()}
                </p>
                <p className="mb-0">
                  <strong>Stack:</strong>
                </p>
                <pre className="text-sm">
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

PaymentErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  onBack: PropTypes.func,
}

export default PaymentErrorBoundary
